// Workflow Drafts widget — shows saved drafts the admin can resume from
// the dashboard or Action Center. Backed by assistant_workflow_drafts.
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { Loader2, Play, Trash2, ListChecks } from "lucide-react";
import type { WorkflowView } from "./CurrentWorkflowPanel";

const TYPE_LABEL: Record<string, string> = {
  instructor_onboarding: "Instructor Onboarding",
  class_creation: "Class Creation",
  instructor_assignment: "Instructor Assignment",
  popup_vendor: "Pop-Up Vendor",
  event_planning: "Event Planning",
  parent_onboarding: "Parent Onboarding",
  student_onboarding: "Student Onboarding",
  meeting_planning: "Meeting Planning",
};

const STATUS_LABEL: Record<string, string> = {
  draft: "In progress",
  waiting_for_info: "Waiting on info",
  ready_for_review: "Ready to review",
  ready_to_execute: "Ready to go",
};


interface Props {
  functionName?: string;
  compact?: boolean;
}

export function WorkflowDraftsWidget({ functionName = "silver-sage-assistant", compact = false }: Props) {
  const [drafts, setDrafts] = useState<WorkflowView[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = async () => {
    const { data, error } = await supabase.functions.invoke(functionName, { body: { action: "workflow_list" } });
    if (error) { toast.error(error.message); return; }
    setDrafts(data?.drafts ?? []);
  };
  useEffect(() => { load(); }, []);

  const resume = async (id: string) => {
    setBusyId(id);
    try {
      await supabase.functions.invoke(functionName, { body: { action: "workflow_resume", draft_id: id } });
      toast.success("Draft resumed. Open the assistant to continue.");
      await load();
    } catch (e: any) { toast.error(e.message); }
    finally { setBusyId(null); }
  };
  const del = async (id: string) => {
    if (!confirm("Delete this workflow draft?")) return;
    setBusyId(id);
    try {
      await supabase.functions.invoke(functionName, { body: { action: "workflow_delete", draft_id: id } });
      await load();
    } catch (e: any) { toast.error(e.message); }
    finally { setBusyId(null); }
  };

  return (
    <Card className={compact ? "p-3" : "p-4"}>
      <div className="flex items-center gap-2 mb-2">
        <ListChecks className="h-4 w-4 text-primary" />
        <h3 className="font-semibold text-sm">Picking up where you left off</h3>
        {drafts && drafts.length > 0 && <Badge variant="secondary" className="ml-auto text-[10px]">{drafts.length}</Badge>}
      </div>
      {drafts === null ? (
        <div className="text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</div>
      ) : drafts.length === 0 ? (
        <p className="text-xs text-muted-foreground">Nothing in progress right now. Start a new task in MAVIS whenever you're ready.</p>

      ) : (
        <ul className="space-y-2">
          {drafts.map((d) => (
            <li key={d.id} className="rounded border p-2 flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[10px]">{TYPE_LABEL[d.workflow_type] ?? d.workflow_type}</Badge>
                <span className="font-medium text-sm truncate flex-1">{d.title}</span>
                <span className="text-xs text-muted-foreground tabular-nums">{d.progress}%</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>{STATUS_LABEL[d.status] ?? d.status}</span>
                <span>·</span>
                <span>Updated {formatDistanceToNow(new Date(d.updated_at), { addSuffix: true })}</span>
                {d.waiting_on && (<><span>·</span><span className="italic">Waiting on: {d.waiting_on}</span></>)}
              </div>
              <div className="flex gap-1 pt-1">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => resume(d.id)} disabled={busyId === d.id}>
                  <Play className="h-3 w-3 mr-1" /> Resume
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => del(d.id)} disabled={busyId === d.id}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
